import SessionTelemetry from '../store/schema/SessionTelemetry';
import { SmartComposeInteraction } from '@qas/smartcompose-tslibrary/dist/src/smartComposeInteraction';
import ExtendedSmartComposeInteraction from '../store/schema/ExtendedSmartComposeInteraction';
import { ISmartComposeSession } from '@qas/smartcompose-tslibrary/dist/src/session';
import { trace } from 'owa-trace';
import { logUsage } from 'owa-analytics';
import { getISOString } from 'owa-datetime';

const SESSION_START_EVENT_NAME = 'SmartComposeSessionStart';
const SESSION_CREATION_FAILED_EVENT_NAME = 'SmartComposeSessionCreationFailed';
const SESSION_END_EVENT_NAME = 'SmartComposeSessionEnd';
const REQUEST_SUGGESTION_EVENT_NAME = 'SmartComposeRequestSuggestion';
const SHOW_SUGGESTION_EVENT_NAME = 'SmartComposeShowSuggestion';
const ACCEPT_SUGGESTION_EVENT_NAME = 'SmartComposeAcceptSuggestion';
const REJECT_SUGGESTION_EVENT_NAME = 'SmartComposeRejectSuggestion';
const PERSONALIZATION_SERVICE_ERROR_EVENT_NAME = 'SmartComposePersonalizationError';
const SUGGESTION_NOT_RENDERED = 'SmartComposeSuggestionNotRendered';

/**** Notes about eyes-on and eyes-off data logging in this file *****
 * The SessionTelemetry and SmartComposeInteraction objects contain all of the telemetry data that we want to log about
 * SmartCompose sessions and suggestions.
 * Some of the properties are considered eyes-on because they contain no personal
 * information: they are just metadata about suggestions (e.g. latency for suggestion returned, number of suggestions shown/
 * accepted/rejected, etc.). These proprties can be logged though the normal logUsage pipeline.
 * Other properties are considered eyes-off because they do contain personal data (e.g. the prefix of the email that prompted
 * the suggestion). These properties are logged through SIGS by calling addSuggestionInfo on the SmartComposeSession.
 *********/

export function logSessionCreationFailed() {
    trace.info('[smartcompose dp] session creation failed');

    logUsage(SESSION_CREATION_FAILED_EVENT_NAME);
}

export function logSessionStardAndEnd(sessionTelemetry: SessionTelemetry) {
    trace.info('[smartcompose dp] start session: ' + JSON.stringify(sessionTelemetry));
    trace.info('[smartcompose dp] end session: ' + JSON.stringify(sessionTelemetry));

    // eyes-on data
    logUsage(
        SESSION_START_EVENT_NAME,
        [
            sessionTelemetry.sessionId,
            sessionTelemetry.composeType,
            sessionTelemetry.sessionStartTime
                ? getISOString(sessionTelemetry.sessionStartTime)
                : null,
            sessionTelemetry.showSuggestions,
            sessionTelemetry.userCulture,
            0 /* via AL */,
        ],
        { isCore: true, excludeFromKusto: true }
    );

    // eyes-on data
    logUsage(
        SESSION_END_EVENT_NAME,
        [
            sessionTelemetry.sessionId,
            sessionTelemetry.sessionEndTime ? getISOString(sessionTelemetry.sessionEndTime) : null,
            sessionTelemetry.suggestionsRequested,
            sessionTelemetry.suggestionsReceived,
            sessionTelemetry.suggestionsShown,
            sessionTelemetry.suggestionsAccepted,
            sessionTelemetry.suggestionsRejected,
            sessionTelemetry.modelVersion,
            0 /* totalCharactersTypedInSession. This is not logged because of privacy concerns */,
            sessionTelemetry.requestsSentWithNoDelay,
            sessionTelemetry.requestsSentAfterDelay,
            sessionTelemetry.requestsAborted,
            0 /* via AL */,
        ],
        { isCore: true, excludeFromKusto: true }
    );
}

export function logRequestSuggestion(
    suggestionTelemetry: SmartComposeInteraction,
    currentSmartComposeSession: ISmartComposeSession
) {
    trace.info('[smartcompose dp] request suggestion: ' + JSON.stringify(suggestionTelemetry));

    // eyes-on data
    // don't log every request suggestion. This is too much traffic. Instead we'll log session summaries in sessionEnd, and only
    // log here when a suggestion was returned
    if (suggestionTelemetry.suggestionReturned) {
        logUsage(
            REQUEST_SUGGESTION_EVENT_NAME,
            [
                suggestionTelemetry.sessionId,
                suggestionTelemetry.suggestionTraceId,
                suggestionTelemetry.suggestionRequestTime
                    ? suggestionTelemetry.suggestionRequestTime.toISOString()
                    : null,
                suggestionTelemetry.totalResponseLatency,
                suggestionTelemetry.serviceLatency,
                suggestionTelemetry.suggestionReturned,
                suggestionTelemetry.suggestionLength,
                suggestionTelemetry.suggestionSource,
                0 /* via AL */,
            ],
            { isCore: true, excludeFromKusto: true }
        );
    }

    // eyes-off data. Only log if a suggestion was returned because logging every requested suggestion
    // leads to too much data being sent to SIGS
    if (currentSmartComposeSession && suggestionTelemetry.suggestionReturned) {
        currentSmartComposeSession.addSuggestionInfo(suggestionTelemetry);
    }
}

export function logSuggestionShown(
    suggestionTelemetry: ExtendedSmartComposeInteraction,
    currentSmartComposeSession: ISmartComposeSession
) {
    trace.info('[smartcompose dp] suggestion shown: ' + JSON.stringify(suggestionTelemetry));

    // eyes-on data
    logUsage(
        SHOW_SUGGESTION_EVENT_NAME,
        [
            suggestionTelemetry.sessionId,
            suggestionTelemetry.suggestionTraceId,
            suggestionTelemetry.initialRenderTime
                ? suggestionTelemetry.initialRenderTime.toISOString()
                : null,
            0 /* charactersTypedInSessionAtShownTime: this is not logged because of privacy concerns */,
            suggestionTelemetry.numberOfCharactersRendered,
            0 /* via AL */,
        ],
        { isCore: true, excludeFromKusto: true }
    );

    // eyes-off data
    if (currentSmartComposeSession) {
        currentSmartComposeSession.addSuggestionInfo(suggestionTelemetry);
    }
}

export function logSuggestionAccepted(
    suggestionTelemetry: SmartComposeInteraction,
    currentSmartComposeSession: ISmartComposeSession,
    keyPressedForAccept: number
) {
    trace.info('[smartcompose dp] suggestion accepted: ' + JSON.stringify(suggestionTelemetry));

    // eyes-on data
    logUsage(
        ACCEPT_SUGGESTION_EVENT_NAME,
        [
            suggestionTelemetry.sessionId,
            suggestionTelemetry.suggestionTraceId,
            suggestionTelemetry.suggestionAcceptanceTime
                ? suggestionTelemetry.suggestionAcceptanceTime.toISOString()
                : null,
            suggestionTelemetry.acceptanceReason,
            suggestionTelemetry.suggestionLength,
            suggestionTelemetry.numberOfMatchingCharactersTyped,
            keyPressedForAccept,
            0 /* via AL */,
        ],
        { isCore: true, excludeFromKusto: true }
    );

    // eyes-off data
    if (currentSmartComposeSession) {
        currentSmartComposeSession.addSuggestionInfo(suggestionTelemetry);
    }
}

export function logSuggestionRejected(
    suggestionTelemetry: SmartComposeInteraction,
    currentSmartComposeSession: ISmartComposeSession
) {
    trace.info('[smartcompose dp] suggestion rejected: ' + JSON.stringify(suggestionTelemetry));

    // eyes-on data
    logUsage(
        REJECT_SUGGESTION_EVENT_NAME,
        [
            suggestionTelemetry.sessionId,
            suggestionTelemetry.suggestionTraceId,
            suggestionTelemetry.suggestionRejectionTime
                ? suggestionTelemetry.suggestionRejectionTime.toISOString()
                : null,
            suggestionTelemetry.rejectionReason,
            suggestionTelemetry.numberOfMatchingCharactersTyped,
            0 /* via AL */,
        ],
        { isCore: true, excludeFromKusto: true }
    );

    // eyes-off data
    if (currentSmartComposeSession) {
        currentSmartComposeSession.addSuggestionInfo(suggestionTelemetry);
    }
}

export function logPersonalizationServiceError(message: string) {
    logUsage(PERSONALIZATION_SERVICE_ERROR_EVENT_NAME, { detail: message }, { logEvery: 1 });
}

export function logSuggestionNotRendered(suggestionTelemetry: SmartComposeInteraction) {
    trace.info('[smartcompose dp] suggestion not shown: ' + JSON.stringify(suggestionTelemetry));

    // eyes-on data
    logUsage(
        SUGGESTION_NOT_RENDERED,
        [
            suggestionTelemetry.sessionId,
            suggestionTelemetry.suggestionTraceId,
            0 /* charactersTypedInSessionAtShownTime: this is not logged because of privacy concerns */,
            0 /* via AL */,
        ],
        { isCore: true, excludeFromKusto: true }
    );
}
